import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowUpTrayIcon,
  BellAlertIcon,
  CheckCircleIcon,
  DocumentArrowUpIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
  PencilSquareIcon,
  PhotoIcon,
  PlusIcon,
  PrinterIcon,
  TrashIcon,
  TruckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import vehicleMaintenanceApi from '../../api/vehicleMaintenanceApi';
import { referenceAPI } from '../../api/api';
import { useAuth } from '../../contexts/AuthContext';
import { sortRowsByVehicleCode } from './internalAuditVehicleCodeSort';
import { exportVehiclesToExcel, parseVehicleImportFile, resolveImportHotelId, printVehicleSummary } from './exportVehiclesExcel';
import {
  StatusBadge,
  VehicleEmptyState,
  VehicleFilterChips,
  VehicleFormTabs,
  VehicleModuleHero,
  VehiclePageShell,
  VehiclePersonCell,
  VehiclePlateCell,
  vehiclePlateColumnProps,
  VehicleSelectionPreview,
  VehicleStatCard,
  VehicleStatGrid,
} from './vehicleMaintenanceUi';
import ActionToolbar from '../../components/ui/ActionToolbar';
import IconActionButton from '../../components/ui/IconActionButton';
import DataTable from '../../components/ui/DataTable';
import FilterPanel from '../../components/ui/FilterPanel';
import FilterActions from '../../components/ui/FilterActions';
import ResponsiveModal from '../../components/ui/ResponsiveModal';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';

const initialForm = {
  vehicle_code: '',
  city: '',
  hotel_id: '',
  license_plate: '',
  position: '',
  assigned_person: '',
  custody_date: '',
  custody_department: '',
  vehicle_type: '',
  transport_type: '',
  brand: '',
  model: '',
  package_name: '',
  transmission: '',
  fuel_type: '',
  model_year: '',
  license_number: '',
  registration_date: '',
  engine_number: '',
  chassis_number: '',
  traffic_policy_date: '',
  insurance_policy_date: '',
  first_inspection_date: '',
  exhaust_inspection_date: '',
  current_km: '',
  is_active: true,
};

const vehicleFormKeys = Object.keys(initialForm);
const vehicleFormKeysForMultipart = vehicleFormKeys.filter(
  (k) => k !== 'first_inspection_date' && k !== 'exhaust_inspection_date',
);

const buildVehiclePayload = (state) => ({
  ...state,
  model_year: state.model_year ? Number(state.model_year) : null,
  current_km: state.current_km ? Number(state.current_km) : null,
  custody_department: state.custody_department ? Number(state.custody_department) : null,
  custody_date: state.custody_date || null,
});

const vehiclePayloadToFormData = (payload) => {
  const form = new FormData();
  vehicleFormKeysForMultipart.forEach((key) => {
    const v = payload[key];
    if (key === 'is_active') {
      form.append(key, v ? 'true' : 'false');
      return;
    }
    if (v === null || v === undefined) {
      form.append(key, '');
      return;
    }
    form.append(key, String(v));
  });
  return form;
};

const normalizePlateInput = (value = '') => {
  const raw = String(value).toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (!raw) return '';

  let index = 0;
  let city = '';
  while (index < raw.length && city.length < 2 && /\d/.test(raw[index])) {
    city += raw[index];
    index += 1;
  }

  let letters = '';
  while (index < raw.length && letters.length < 3 && /[A-Z]/.test(raw[index])) {
    letters += raw[index];
    index += 1;
  }

  const remainder = raw.slice(index);
  const tailMatch = remainder.match(/(\d{1,4})$/);
  const tail = tailMatch ? tailMatch[1] : '';

  return [city, letters, tail].filter(Boolean).join(' ');
};

const formatPlatePreview = (value = '') => normalizePlateInput(value) || '07 ABC 07';

const VEHICLE_FORM_TABS = [
  { id: 'general', label: 'GENEL BİLGİLER' },
  { id: 'license', label: 'RUHSAT BİLGİLERİ' },
  { id: 'insurance', label: 'SİGORTA & KASKO' },
  { id: 'custody', label: 'ZİMMET' },
];

const FIELD_INPUT_CLASS = 'h-9 rounded-lg border-gray-200 dark:border-gray-600 text-sm';
const FIELD_SELECT_CLASS =
  'flex h-9 w-full rounded-lg border border-gray-200 bg-white px-2.5 text-sm text-gray-900 shadow-sm transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-800 dark:text-white';

/** Module-level: defining this inside the page remounts sections on every keystroke (scroll/focus jump). */
function FormSection({ title, description, children, className = '' }) {
  return (
    <section className={`rounded-xl border border-gray-200/90 bg-white shadow-sm dark:border-gray-700/90 dark:bg-gray-800/60 ${className}`}>
      <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-700/80">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
        {description ? (
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{description}</p>
        ) : null}
      </div>
      <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 xl:grid-cols-3">
        {children}
      </div>
    </section>
  );
}

function VehicleFormField({ name, label, value, onChange, colSpan = '', readOnly = false, placeholder = '' }) {
  const isReadOnlyInspection = name === 'first_inspection_date' || name === 'exhaust_inspection_date';
  const isDisabled = readOnly || isReadOnlyInspection;
  const inputType = name.includes('date')
    ? 'date'
    : (name.includes('year') || name === 'current_km' ? 'number' : 'text');

  return (
    <div className={`space-y-1.5 ${colSpan}`}>
      <Label className="text-xs font-medium text-gray-600 dark:text-gray-300">{label}</Label>
      <Input
        type={inputType}
        name={name}
        placeholder={placeholder}
        value={value ?? ''}
        readOnly={isDisabled}
        disabled={isDisabled}
        className={`${FIELD_INPUT_CLASS} ${isDisabled ? 'cursor-not-allowed bg-gray-50 dark:bg-gray-900/50' : 'bg-white dark:bg-gray-800'}`}
        onChange={onChange}
      />
    </div>
  );
}

const InternalAuditVehiclesPage = () => {
  const { user } = useAuth();
  const userHotelId = user?.hotel?.id ?? user?.hotel ?? null;
  const [hotels, setHotels] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [rows, setRows] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    cities: [],
    hotels: [],
    locations: [],
    brands: [],
    fuel_types: [],
    transport_types: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeFormTab, setActiveFormTab] = useState('general');
  const [quickFilter, setQuickFilter] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    city: '',
    hotel_id: '',
    brand: '',
    fuel_type: '',
    transport_type: '',
    is_active: 'true',
  });
  const [draftFilters, setDraftFilters] = useState({
    search: '',
    city: '',
    hotel_id: '',
    brand: '',
    fuel_type: '',
    transport_type: '',
    is_active: 'true',
  });
  const [formData, setFormData] = useState(initialForm);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
  const [custodyFormFile, setCustodyFormFile] = useState(null);
  const [custodyFormName, setCustodyFormName] = useState('');
  const photoInputRef = useRef(null);
  const custodyFormInputRef = useRef(null);
  const importInputRef = useRef(null);
  const [importing, setImporting] = useState(false);

  const loadData = async (activeFilters = filters) => {
    setLoading(true);
    setError(null);
    try {
      const [vehicleResponse, optionsResponse] = await Promise.all([
        vehicleMaintenanceApi.getVehicles(activeFilters),
        vehicleMaintenanceApi.getVehicleFilterOptions(),
      ]);

      setRows(vehicleResponse?.data || []);
      setFilterOptions(optionsResponse?.data || {
        cities: [],
        hotels: [],
        locations: [],
        brands: [],
        fuel_types: [],
        transport_types: [],
      });
      setSelectedIds([]);
    } catch (err) {
      setError(err.message || 'Araç kayıtları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    referenceAPI.getHotels()
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        setHotels(list);
      })
      .catch(() => setHotels([]));
  }, []);

  const selectableHotels = useMemo(() => hotels, [hotels]);

  const selectedHotelName = useMemo(() => {
    const id = formData.hotel_id;
    if (!id) return '';
    const hotel = hotels.find((h) => String(h.id) === String(id));
    return hotel?.hotel_name || '';
  }, [formData.hotel_id, hotels]);

  useEffect(() => {
    const hotelId = formData.hotel_id;
    if (!hotelId) {
      setDepartments([]);
      return;
    }
    referenceAPI.getDepartments(hotelId)
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        setDepartments(list);
      })
      .catch(() => setDepartments([]));
  }, [formData.hotel_id]);

  const handleSearch = () => {
    setFilters(draftFilters);
    loadData(draftFilters);
  };

  const handleClearFilters = () => {
    const resetFilters = {
      search: '',
      city: '',
      hotel_id: '',
      brand: '',
      fuel_type: '',
      transport_type: '',
      is_active: 'true',
    };
    setDraftFilters(resetFilters);
    setFilters(resetFilters);
    setQuickFilter(null);
    loadData(resetFilters);
  };

  const applyFilterUpdate = (nextFilters) => {
    setFilters(nextFilters);
    setDraftFilters(nextFilters);
    loadData(nextFilters);
  };

  const handleExportExcel = async () => {
    try {
      const rowsToExport = selectedIds.length
        ? displayRows.filter((row) => selectedIds.includes(row.id))
        : displayRows;
      await exportVehiclesToExcel(rowsToExport);
    } catch (err) {
      setError(err.message || 'Excel dosyası oluşturulamadı.');
    }
  };

  const handleBulkDeactivate = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`${selectedIds.length} aracı pasife almak istediğinize emin misiniz?`)) return;
    try {
      await vehicleMaintenanceApi.bulkDeactivateVehicles(selectedIds);
      setSelectedRow(null);
      await loadData(filters);
    } catch (err) {
      setError(err.message || 'Toplu pasife alma başarısız.');
    }
  };

  const handleRefresh = () => {
    loadData(filters);
  };

  const handlePrintSelected = () => {
    const fromChecks = selectedIds.length
      ? displayRows.filter((row) => selectedIds.includes(row.id))
      : [];
    const vehiclesToPrint = fromChecks.length ? fromChecks : (selectedRow ? [selectedRow] : []);
    if (!vehiclesToPrint.length) {
      setError('Yazdırmak için tablodan satır seçin veya işaretleyin.');
      return;
    }
    try {
      printVehicleSummary(vehiclesToPrint);
    } catch (err) {
      setError(err.message || 'Yazdırma başlatılamadı.');
    }
  };

  const handleImportExcel = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setError(null);
    try {
      const parsedRows = await parseVehicleImportFile(file);
      if (!parsedRows.length) {
        throw new Error('İçe aktarılacak satır bulunamadı.');
      }

      const hotelList = hotels.length ? hotels : (filterOptions.hotels || []);
      let successCount = 0;
      const failures = [];

      for (const row of parsedRows) {
        if (!row.vehicle_code || !row.license_plate) {
          failures.push(`Satır ${row._rowNumber}: Araç ID ve Plaka zorunlu.`);
          continue;
        }
        const hotelId = resolveImportHotelId(row, hotelList, userHotelId);
        if (!hotelId) {
          failures.push(`Satır ${row._rowNumber}: Tesis bulunamadı (${row.hotel_name || row.hotel_id || 'boş'}).`);
          continue;
        }

        try {
          await vehicleMaintenanceApi.createVehicle({
            vehicle_code: row.vehicle_code,
            license_plate: row.license_plate,
            hotel_id: hotelId,
            city: row.city || '',
            position: row.position || '',
            assigned_person: row.assigned_person || '',
            vehicle_type: row.vehicle_type || '',
            transport_type: row.transport_type || '',
            brand: row.brand || '',
            model: row.model || '',
            package_name: row.package_name || '',
            transmission: row.transmission || '',
            fuel_type: row.fuel_type || '',
            model_year: Number.isFinite(row.model_year) ? row.model_year : null,
            license_number: row.license_number || '',
            registration_date: row.registration_date,
            engine_number: row.engine_number || '',
            chassis_number: row.chassis_number || '',
            traffic_policy_date: row.traffic_policy_date,
            insurance_policy_date: row.insurance_policy_date,
            current_km: Number.isFinite(row.current_km) ? row.current_km : null,
            is_active: row.is_active !== false,
          });
          successCount += 1;
        } catch (err) {
          failures.push(`Satır ${row._rowNumber} (${row.license_plate}): ${err.message || 'Kayıt oluşturulamadı.'}`);
        }
      }

      await loadData(filters);

      if (failures.length) {
        setError(
          `${successCount} kayıt eklendi, ${failures.length} hata:\n${failures.slice(0, 5).join('\n')}${
            failures.length > 5 ? `\n… +${failures.length - 5} hata daha` : ''
          }`,
        );
      } else {
        window.alert(`${successCount} araç kaydı başarıyla içe aktarıldı.`);
      }
    } catch (err) {
      setError(err.message || 'Excel içe aktarma başarısız.');
    } finally {
      setImporting(false);
      if (importInputRef.current) importInputRef.current.value = '';
    }
  };

  const resetPhotoState = () => {
    if (photoPreviewUrl && photoPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(photoPreviewUrl);
    }
    setPhotoFile(null);
    setPhotoPreviewUrl(null);
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const resetCustodyFormState = () => {
    setCustodyFormFile(null);
    setCustodyFormName('');
    if (custodyFormInputRef.current) custodyFormInputRef.current.value = '';
  };

  const openCreateModal = () => {
    resetPhotoState();
    resetCustodyFormState();
    setEditingRow(null);
    setSelectedRow(null);
    setFormData({
      ...initialForm,
      hotel_id: userHotelId ? String(userHotelId) : '',
    });
    setActiveFormTab('general');
    setIsModalOpen(true);
  };

  const openEditModal = (row) => {
    if (!row) return;
    resetPhotoState();
    resetCustodyFormState();
    setEditingRow(row);
    setPhotoPreviewUrl(row.photo_url || null);
    const custodyPath = row.custody_form_url || row.custody_form || '';
    setCustodyFormName(custodyPath ? custodyPath.split('/').pop() : '');
    setFormData({
      vehicle_code: row.vehicle_code || '',
      city: row.city || '',
      hotel_id: row.hotel ? String(row.hotel) : '',
      license_plate: row.license_plate || '',
      position: row.position || '',
      assigned_person: row.assigned_person || '',
      custody_date: row.custody_date || '',
      custody_department: row.custody_department ? String(row.custody_department) : '',
      vehicle_type: row.vehicle_type || '',
      transport_type: row.transport_type || '',
      brand: row.brand || '',
      model: row.model || '',
      package_name: row.package_name || '',
      transmission: row.transmission || '',
      fuel_type: row.fuel_type || '',
      model_year: row.model_year || '',
      license_number: row.license_number || '',
      registration_date: row.registration_date || '',
      engine_number: row.engine_number || '',
      chassis_number: row.chassis_number || '',
      traffic_policy_date: row.traffic_policy_date || '',
      insurance_policy_date: row.insurance_policy_date || '',
      first_inspection_date: row.first_inspection_date || '',
      exhaust_inspection_date: row.exhaust_inspection_date || '',
      current_km: row.current_km || '',
      is_active: row.is_active ?? true,
    });
    setActiveFormTab('general');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    resetPhotoState();
    resetCustodyFormState();
    setIsModalOpen(false);
    setActiveFormTab('general');
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (photoPreviewUrl && photoPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(photoPreviewUrl);
    }
    setPhotoFile(file);
    setPhotoPreviewUrl(URL.createObjectURL(file));
  };

  const handleCustodyFormChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setCustodyFormFile(file);
    setCustodyFormName(file.name);
  };

  const handleDelete = async (row) => {
    if (!row) return;
    if (!window.confirm(`"${row.license_plate}" aracını silmek istediğinize emin misiniz?`)) return;

    try {
      await vehicleMaintenanceApi.deleteVehicle(row.id);
      setSelectedRow(null);
      await loadData();
    } catch (err) {
      setError(err.message || 'Araç silinemedi.');
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const payload = buildVehiclePayload(formData);
      const hasUploads = photoFile || custodyFormFile;

      if (hasUploads) {
        const fd = vehiclePayloadToFormData(payload);
        if (photoFile) fd.append('photo', photoFile);
        if (custodyFormFile) fd.append('custody_form', custodyFormFile);
        if (editingRow?.id) {
          await vehicleMaintenanceApi.updateVehicle(editingRow.id, fd);
        } else {
          await vehicleMaintenanceApi.createVehicle(fd);
        }
      } else if (editingRow?.id) {
        await vehicleMaintenanceApi.updateVehicle(editingRow.id, payload);
      } else {
        await vehicleMaintenanceApi.createVehicle(payload);
      }

      resetPhotoState();
      resetCustodyFormState();
      setIsModalOpen(false);
      setEditingRow(null);
      setSelectedRow(null);
      setFormData(initialForm);
      await loadData();
    } catch (err) {
      setError(err.message || 'Araç kaydı kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(() => [
    {
      id: 'select',
      header: () => (
        <input
          type="checkbox"
          checked={rows.length > 0 && rows.every((row) => selectedIds.includes(row.id))}
          onChange={() => {
            const ids = rows.map((row) => row.id);
            const allSelected = ids.length > 0 && ids.every((id) => selectedIds.includes(id));
            setSelectedIds(allSelected ? [] : ids);
          }}
          onClick={(e) => e.stopPropagation()}
          title="Tümünü seç"
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={selectedIds.includes(row.original.id)}
          onChange={() => {
            const id = row.original.id;
            setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
          }}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      size: 36,
      enableSorting: false,
    },
    {
      header: 'Araç ID',
      accessorKey: 'vehicle_code',
      cell: ({ row }) => (
        <span className="rounded bg-slate-100 px-1 py-0 font-mono text-[10px] font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
          {row.original.vehicle_code || '—'}
        </span>
      ),
    },
    {
      header: 'Şehir',
      accessorKey: 'city',
      cell: ({ getValue }) => getValue() || '—',
    },
    {
      header: 'Tesis',
      accessorKey: 'hotel_name',
      cell: ({ getValue }) => getValue() || '—',
    },
    {
      ...vehiclePlateColumnProps,
      cell: ({ row }) => (
        <VehiclePlateCell
          plate={row.original.license_plate}
          brand={row.original.brand}
          model={row.original.model}
        />
      ),
    },
    {
      header: 'Yakıt',
      accessorKey: 'fuel_type',
      cell: ({ getValue }) => getValue() || '—',
    },
    {
      header: 'Model Yılı',
      accessorKey: 'model_year',
      cell: ({ getValue }) => getValue() || '—',
    },
    {
      header: 'KM',
      accessorKey: 'current_km',
      cell: ({ getValue }) => {
        const v = getValue();
        if (v === null || v === undefined || v === '') return '—';
        return Number(v).toLocaleString('tr-TR');
      },
    },
    {
      header: 'Zimmetli',
      accessorKey: 'assigned_person',
      cell: ({ row }) => (
        <VehiclePersonCell
          name={row.original.assigned_person}
          subtitle={row.original.position || undefined}
        />
      ),
    },
    {
      header: 'Trafik',
      accessorKey: 'traffic_follow_up_status',
      cell: ({ getValue }) => <StatusBadge status={getValue()} />,
    },
    {
      header: 'Sigorta',
      accessorKey: 'insurance_follow_up_status',
      cell: ({ getValue }) => <StatusBadge status={getValue()} />,
    },
    {
      header: 'Muayene',
      accessorKey: 'inspection_follow_up_status',
      cell: ({ getValue }) => <StatusBadge status={getValue()} />,
    },
  ], [selectedIds, rows]);

  const sortedRows = useMemo(() => sortRowsByVehicleCode(rows), [rows]);

  const filteredRows = useMemo(() => {
    const normalize = (value) => String(value ?? '').trim().toLocaleLowerCase('tr-TR');
    const search = normalize(filters.search);
    const city = normalize(filters.city);
    const brand = normalize(filters.brand);
    const fuelType = normalize(filters.fuel_type);
    const transportType = normalize(filters.transport_type);
    const hotelId = String(filters.hotel_id || '').trim();
    const isActive = filters.is_active;

    return sortedRows.filter((row) => {
      if (city && normalize(row.city) !== city && !normalize(row.city).includes(city)) {
        return false;
      }
      if (brand && normalize(row.brand) !== brand && !normalize(row.brand).includes(brand)) {
        return false;
      }
      if (fuelType && normalize(row.fuel_type) !== fuelType) {
        return false;
      }
      if (transportType && normalize(row.transport_type) !== transportType) {
        return false;
      }
      if (hotelId && String(row.hotel ?? row.hotel_id ?? '') !== hotelId) {
        return false;
      }
      if (isActive === 'true' && row.is_active === false) {
        return false;
      }
      if (isActive === 'false' && row.is_active !== false) {
        return false;
      }
      if (search) {
        const haystack = [
          row.vehicle_code,
          row.license_plate,
          row.city,
          row.location,
          row.hotel_name,
          row.brand,
          row.model,
          row.assigned_person,
        ].map(normalize).join(' ');
        if (!haystack.includes(search)) return false;
      }
      return true;
    });
  }, [sortedRows, filters]);

  const displayRows = useMemo(() => {
    if (!quickFilter) return filteredRows;
    if (quickFilter === 'activeOk') {
      return filteredRows.filter(
        (row) =>
          row.insurance_follow_up_status === 'active' &&
          row.traffic_follow_up_status === 'active' &&
          row.inspection_follow_up_status === 'active',
      );
    }
    if (quickFilter === 'approaching') {
      return filteredRows.filter((row) => {
        const hasExpired =
          row.insurance_follow_up_status === 'expired' ||
          row.traffic_follow_up_status === 'expired' ||
          row.inspection_follow_up_status === 'expired';
        if (hasExpired) return false;
        return (
          row.insurance_follow_up_status === 'approaching' ||
          row.traffic_follow_up_status === 'approaching' ||
          row.inspection_follow_up_status === 'approaching'
        );
      });
    }
    if (quickFilter === 'expired') {
      return filteredRows.filter(
        (row) =>
          row.insurance_follow_up_status === 'expired' ||
          row.traffic_follow_up_status === 'expired' ||
          row.inspection_follow_up_status === 'expired',
      );
    }
    return filteredRows;
  }, [filteredRows, quickFilter]);

  const filterChips = useMemo(() => {
    const chips = [];
    const quickLabels = {
      activeOk: 'Takibi tamam',
      approaching: 'Süre yaklaşıyor',
      expired: 'Kritik araç',
    };

    if (filters.search) {
      chips.push({
        key: 'search',
        label: `Arama: ${filters.search}`,
        onRemove: () => applyFilterUpdate({ ...filters, search: '' }),
      });
    }
    if (filters.city) {
      chips.push({
        key: 'city',
        label: `Şehir: ${filters.city}`,
        onRemove: () => applyFilterUpdate({ ...filters, city: '' }),
      });
    }
    if (filters.hotel_id) {
      const hotel = (filterOptions.hotels || []).find((item) => String(item.id) === String(filters.hotel_id));
      chips.push({
        key: 'hotel_id',
        label: `Tesis: ${hotel?.hotel_name || filters.hotel_id}`,
        onRemove: () => applyFilterUpdate({ ...filters, hotel_id: '' }),
      });
    }
    if (filters.brand) {
      chips.push({
        key: 'brand',
        label: `Marka: ${filters.brand}`,
        onRemove: () => applyFilterUpdate({ ...filters, brand: '' }),
      });
    }
    if (filters.fuel_type) {
      chips.push({
        key: 'fuel_type',
        label: `Yakıt: ${filters.fuel_type}`,
        onRemove: () => applyFilterUpdate({ ...filters, fuel_type: '' }),
      });
    }
    if (filters.transport_type) {
      chips.push({
        key: 'transport_type',
        label: `Taşıt: ${filters.transport_type}`,
        onRemove: () => applyFilterUpdate({ ...filters, transport_type: '' }),
      });
    }
    if (filters.is_active && filters.is_active !== 'true') {
      chips.push({
        key: 'is_active',
        label: 'Durum: Pasif',
        onRemove: () => applyFilterUpdate({ ...filters, is_active: 'true' }),
      });
    }
    if (quickFilter) {
      chips.push({
        key: 'quickFilter',
        label: quickLabels[quickFilter] || quickFilter,
        onRemove: () => setQuickFilter(null),
      });
    }
    return chips;
  }, [filters, quickFilter, filterOptions.hotels]);

  const stats = useMemo(() => {
    const total = filteredRows.length;
    const expired = filteredRows.filter(
      (r) =>
        r.insurance_follow_up_status === 'expired' ||
        r.traffic_follow_up_status === 'expired' ||
        r.inspection_follow_up_status === 'expired',
    ).length;
    const approaching = filteredRows.filter((r) => {
      const hasExpired =
        r.insurance_follow_up_status === 'expired' ||
        r.traffic_follow_up_status === 'expired' ||
        r.inspection_follow_up_status === 'expired';
      if (hasExpired) return false;
      return (
        r.insurance_follow_up_status === 'approaching' ||
        r.traffic_follow_up_status === 'approaching' ||
        r.inspection_follow_up_status === 'approaching'
      );
    }).length;
    const activeOk = filteredRows.filter(
      (r) =>
        r.insurance_follow_up_status === 'active' &&
        r.traffic_follow_up_status === 'active' &&
        r.inspection_follow_up_status === 'active',
    ).length;
    return { total, expired, approaching, activeOk };
  }, [filteredRows]);

  const getRowClassName = (row) => {
    if (
      row.insurance_follow_up_status === 'expired' ||
      row.traffic_follow_up_status === 'expired' ||
      row.inspection_follow_up_status === 'expired'
    ) {
      return 'border-l-[3px] border-l-rose-400 dark:border-l-rose-500';
    }
    if (
      row.insurance_follow_up_status === 'approaching' ||
      row.traffic_follow_up_status === 'approaching' ||
      row.inspection_follow_up_status === 'approaching'
    ) {
      return 'border-l-[3px] border-l-amber-400 dark:border-l-amber-500';
    }
    return '';
  };

  const fieldSelectClass = FIELD_SELECT_CLASS;
  const fieldInputClass = FIELD_INPUT_CLASS;

  const handleFormFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <VehiclePageShell>
        <VehicleModuleHero
          icon={TruckIcon}
          title="Araç kayıtları"
          subtitle="Araç envanteri, zimmet ve takip bilgilerini yönetin."
          accent="teal"
        />

        <VehicleStatGrid>
          <VehicleStatCard
            label="Toplam Araç"
            value={stats.total}
            tone="slate"
            icon={TruckIcon}
            active={!quickFilter}
            onClick={() => setQuickFilter(null)}
          />
          <VehicleStatCard
            label="Takibi Tamam"
            value={stats.activeOk}
            tone="emerald"
            icon={CheckCircleIcon}
            active={quickFilter === 'activeOk'}
            onClick={() => setQuickFilter((prev) => (prev === 'activeOk' ? null : 'activeOk'))}
          />
          <VehicleStatCard
            label="Süre Yaklaşıyor"
            value={stats.approaching}
            tone="amber"
            icon={BellAlertIcon}
            active={quickFilter === 'approaching'}
            onClick={() => setQuickFilter((prev) => (prev === 'approaching' ? null : 'approaching'))}
          />
          <VehicleStatCard
            label="Kritik Araç"
            value={stats.expired}
            tone="rose"
            icon={ExclamationTriangleIcon}
            active={quickFilter === 'expired'}
            onClick={() => setQuickFilter((prev) => (prev === 'expired' ? null : 'expired'))}
          />
        </VehicleStatGrid>

        {selectedRow ? (
          <VehicleSelectionPreview
            vehicle={selectedRow}
            personName={selectedRow.assigned_person}
            onClear={() => setSelectedRow(null)}
          />
        ) : null}

        {error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
            <div className="flex items-center justify-between gap-3">
              <span className="whitespace-pre-line text-sm">{error}</span>
              <button type="button" onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : null}

        <FilterPanel compact title="Filtreler" isOpen={isFilterOpen} onToggle={() => setIsFilterOpen((prev) => !prev)}>
          <div className="rounded-md border border-teal-100/80 bg-gradient-to-br from-white to-teal-50/40 p-1.5 dark:border-teal-900/40 dark:from-gray-800 dark:to-teal-950/20">
            <div className="grid grid-cols-2 gap-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7">
                <fieldset className="rounded border border-gray-200 bg-white px-1 pb-0 pt-0 dark:border-gray-600 dark:bg-gray-700">
                  <legend className="px-0.5 text-[8px] text-gray-500 dark:text-gray-400 leading-none">Arama</legend>
                  <Input className="h-5 border-0 bg-transparent px-0 text-[10px]" value={draftFilters.search} onChange={(e) => setDraftFilters((prev) => ({ ...prev, search: e.target.value }))} placeholder="Plaka, marka..." />
                </fieldset>
                <fieldset className="rounded border border-gray-200 bg-white px-1 pb-0 pt-0 dark:border-gray-600 dark:bg-gray-700">
                  <legend className="px-0.5 text-[8px] text-gray-500 dark:text-gray-400 leading-none">Şehir</legend>
                  <select className="h-5 w-full bg-transparent text-[10px] text-gray-900 outline-none dark:text-white" value={draftFilters.city} onChange={(e) => setDraftFilters((prev) => ({ ...prev, city: e.target.value }))}>
                    <option value="">Tümü</option>
                    {filterOptions.cities.map((item) => <option key={item} value={item}>{item}</option>)}
                  </select>
                </fieldset>
                <fieldset className="rounded border border-gray-200 bg-white px-1 pb-0 pt-0 dark:border-gray-600 dark:bg-gray-700">
                  <legend className="px-0.5 text-[8px] text-gray-500 dark:text-gray-400 leading-none">Tesis</legend>
                  <select className="h-5 w-full bg-transparent text-[10px] text-gray-900 outline-none dark:text-white" value={draftFilters.hotel_id} onChange={(e) => setDraftFilters((prev) => ({ ...prev, hotel_id: e.target.value }))}>
                    <option value="">Tümü</option>
                    {(filterOptions.hotels || []).map((item) => (
                      <option key={item.id} value={item.id}>{item.hotel_name}</option>
                    ))}
                  </select>
                </fieldset>
                <fieldset className="rounded border border-gray-200 bg-white px-1 pb-0 pt-0 dark:border-gray-600 dark:bg-gray-700">
                  <legend className="px-0.5 text-[8px] text-gray-500 dark:text-gray-400 leading-none">Marka</legend>
                  <select className="h-5 w-full bg-transparent text-[10px] text-gray-900 outline-none dark:text-white" value={draftFilters.brand} onChange={(e) => setDraftFilters((prev) => ({ ...prev, brand: e.target.value }))}>
                    <option value="">Tümü</option>
                    {filterOptions.brands.map((item) => <option key={item} value={item}>{item}</option>)}
                  </select>
                </fieldset>
                <fieldset className="rounded border border-gray-200 bg-white px-1 pb-0 pt-0 dark:border-gray-600 dark:bg-gray-700">
                  <legend className="px-0.5 text-[8px] text-gray-500 dark:text-gray-400 leading-none">Yakıt Türü</legend>
                  <select className="h-5 w-full bg-transparent text-[10px] text-gray-900 outline-none dark:text-white" value={draftFilters.fuel_type} onChange={(e) => setDraftFilters((prev) => ({ ...prev, fuel_type: e.target.value }))}>
                    <option value="">Tümü</option>
                    {filterOptions.fuel_types.map((item) => <option key={item} value={item}>{item}</option>)}
                  </select>
                </fieldset>
                <fieldset className="rounded border border-gray-200 bg-white px-1 pb-0 pt-0 dark:border-gray-600 dark:bg-gray-700">
                  <legend className="px-0.5 text-[8px] text-gray-500 dark:text-gray-400 leading-none">Taşıt Türü</legend>
                  <select className="h-5 w-full bg-transparent text-[10px] text-gray-900 outline-none dark:text-white" value={draftFilters.transport_type} onChange={(e) => setDraftFilters((prev) => ({ ...prev, transport_type: e.target.value }))}>
                    <option value="">Tümü</option>
                    {filterOptions.transport_types.map((item) => <option key={item} value={item}>{item}</option>)}
                  </select>
                </fieldset>
                <fieldset className="rounded border border-gray-200 bg-white px-1 pb-0 pt-0 dark:border-gray-600 dark:bg-gray-700">
                  <legend className="px-0.5 text-[8px] text-gray-500 dark:text-gray-400 leading-none">Durum</legend>
                  <select className="h-5 w-full bg-transparent text-[10px] text-gray-900 outline-none dark:text-white" value={draftFilters.is_active} onChange={(e) => setDraftFilters((prev) => ({ ...prev, is_active: e.target.value }))}>
                    <option value="">Tümü</option>
                    <option value="true">Aktif</option>
                    <option value="false">Pasif</option>
                  </select>
                </fieldset>
              </div>
            </div>

          <div className="mt-1.5 flex justify-end border-t border-gray-100 pt-1.5 dark:border-gray-700">
            <FilterActions compact onClear={handleClearFilters} onSearch={handleSearch} />
          </div>
        </FilterPanel>

        <ActionToolbar className="rounded-xl border border-teal-100/80 bg-white/80 px-2 py-1.5 shadow-sm dark:border-teal-900/40 dark:bg-gray-900/60">
          <IconActionButton icon={PlusIcon} onClick={openCreateModal} title="Yeni Kayıt" />
          <IconActionButton
            icon={PencilSquareIcon}
            onClick={() => openEditModal(selectedRow)}
            title="Düzenle"
            disabled={!selectedRow}
          />
          <IconActionButton
            icon={TrashIcon}
            onClick={() => handleDelete(selectedRow)}
            title="Sil"
            disabled={!selectedRow}
          />
          <IconActionButton
            icon={ArrowPathIcon}
            onClick={handleRefresh}
            title="Yenile"
            disabled={loading}
          />
          <IconActionButton
            icon={PrinterIcon}
            onClick={handlePrintSelected}
            title={selectedIds.length ? `Seçilenleri yazdır (${selectedIds.length})` : 'Seçili aracı yazdır'}
            disabled={!selectedRow && !selectedIds.length}
          />
          <IconActionButton
            icon={ArrowDownTrayIcon}
            onClick={handleExportExcel}
            title={selectedIds.length ? `Seçilenleri Excel indir (${selectedIds.length})` : 'Excel indir'}
            disabled={!displayRows.length && !selectedIds.length}
          />
          <IconActionButton
            icon={NoSymbolIcon}
            onClick={handleBulkDeactivate}
            title="Seçilenleri pasife al"
            disabled={!selectedIds.length}
          />
          <IconActionButton
            icon={ArrowUpTrayIcon}
            onClick={() => importInputRef.current?.click()}
            title="Excel'den içe aktar"
            disabled={importing}
          />
          <input
            ref={importInputRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={handleImportExcel}
          />
        </ActionToolbar>

        <VehicleFilterChips chips={filterChips} onClearAll={filterChips.length ? handleClearFilters : undefined} />

        {!loading && displayRows.length === 0 ? (
          <VehicleEmptyState
            icon={TruckIcon}
            title="Kayıt bulunamadı"
            description={quickFilter || filterChips.length ? 'Filtreleri temizleyerek tüm araçları görebilirsiniz.' : 'Henüz araç kaydı yok. Yeni kayıt ekleyerek başlayın.'}
            action={
              !quickFilter && !filterChips.length ? (
                <Button type="button" size="sm" onClick={openCreateModal}>
                  <PlusIcon className="mr-1.5 h-4 w-4" />
                  Yeni araç ekle
                </Button>
              ) : null
            }
          />
        ) : (
        <DataTable
          data={displayRows}
          columns={columns}
          isLoading={loading}
          showSearch={false}
          onRowClick={setSelectedRow}
          onRowDoubleClick={openEditModal}
          selectedRowId={selectedRow?.id}
          compact
          compactCellWrap
          modern
          softStriped
          getRowClassName={getRowClassName}
        />
        )}

      <ResponsiveModal isOpen={isModalOpen} onClose={closeModal} className="max-w-5xl">
        <div className="flex max-h-[92vh] flex-col">
          <div className="flex items-start justify-between border-b border-gray-100 px-5 py-4 dark:border-gray-700">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingRow ? 'Araç Kaydını Düzenle' : 'Yeni Araç Kaydı'}
              </h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {editingRow
                  ? `${formData.license_plate || '—'} · ${[formData.brand, formData.model].filter(Boolean).join(' ')}`
                  : 'Temel bilgileri, teknik detayları ve zimmet kaydını girin.'}
              </p>
            </div>
            <button type="button" onClick={closeModal} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSave} className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
              <VehicleFormTabs
                tabs={VEHICLE_FORM_TABS}
                activeTab={activeFormTab}
                onChange={setActiveFormTab}
              />

              {activeFormTab === 'general' && (
              <div className="flex flex-col gap-4 rounded-xl border border-gray-200/90 bg-gradient-to-br from-gray-50 to-white p-4 shadow-sm dark:border-gray-700/90 dark:from-gray-800/80 dark:to-gray-900/40 lg:flex-row lg:items-start">
                <div className="w-full lg:w-52 lg:shrink-0 space-y-2">
                  <button
                    type="button"
                    onClick={() => photoInputRef.current?.click()}
                    className="group relative mx-auto flex h-32 w-32 items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-gray-300 bg-white transition hover:border-blue-400 dark:border-gray-600 dark:bg-gray-900/60"
                  >
                    {photoPreviewUrl ? (
                      <img src={photoPreviewUrl} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex flex-col items-center gap-1 text-gray-400 group-hover:text-blue-500">
                        <PhotoIcon className="h-8 w-8" />
                        <span className="text-[10px] font-medium">Fotoğraf</span>
                      </div>
                    )}
                  </button>
                  <input ref={photoInputRef} type="file" accept="image/*" className="sr-only" onChange={handlePhotoChange} />

                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-gray-600 dark:text-gray-300">Plaka</Label>
                    <div className="rounded-lg border border-gray-200 bg-white p-2 shadow-sm dark:border-gray-700 dark:bg-gray-900/40">
                      <div className="mx-auto inline-flex max-w-full items-stretch overflow-hidden rounded-[12px] border border-gray-400/80 bg-white shadow-sm">
                        <div className="flex w-9 shrink-0 flex-col items-center justify-end bg-blue-700 px-1 py-1 text-[8px] font-semibold text-white">
                          <span>TR</span>
                        </div>
                        <div className="flex min-w-[145px] flex-1 items-center justify-center whitespace-nowrap px-2 py-1.5 text-base font-black italic leading-none tracking-[0.08em] text-black">
                          {formatPlatePreview(formData.license_plate)}
                        </div>
                      </div>
                      <Input
                        value={formData.license_plate ?? ''}
                        placeholder="07 ABC 07"
                        maxLength={12}
                        className="mt-2 h-8 rounded-md border-gray-300 bg-white text-sm dark:border-gray-600 dark:bg-gray-800"
                        onChange={(e) => setFormData((prev) => ({ ...prev, license_plate: normalizePlateInput(e.target.value) }))}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-2">
                  <VehicleFormField name="vehicle_code" label="Araç ID" value={formData.vehicle_code} onChange={handleFormFieldChange} />
                  <VehicleFormField name="brand" label="Marka" value={formData.brand} onChange={handleFormFieldChange} />
                  <VehicleFormField name="model" label="Model" value={formData.model} onChange={handleFormFieldChange} />
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-gray-600 dark:text-gray-300">Tesis</Label>
                    <select
                      className={fieldSelectClass}
                      value={formData.hotel_id}
                      onChange={(e) => setFormData((prev) => ({
                        ...prev,
                        hotel_id: e.target.value,
                        custody_department: '',
                      }))}
                      required
                    >
                      <option value="">Tesis seçin</option>
                      {selectableHotels.map((item) => (
                        <option key={item.id} value={item.id}>{item.hotel_name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium text-gray-600 dark:text-gray-300">Durum</Label>
                    <select
                      className={fieldSelectClass}
                      value={String(formData.is_active)}
                      onChange={(e) => setFormData((prev) => ({ ...prev, is_active: e.target.value === 'true' }))}
                    >
                      <option value="true">Aktif</option>
                      <option value="false">Pasif</option>
                    </select>
                  </div>
                </div>
              </div>
              )}

              {activeFormTab === 'general' && (
              <FormSection title="Konum & Kullanım" description="Aracın bulunduğu tesis ve kullanım bilgileri">
                <VehicleFormField name="city" label="Şehir" value={formData.city} onChange={handleFormFieldChange} />
                <VehicleFormField name="position" label="Pozisyon / Görev" value={formData.position} onChange={handleFormFieldChange} />
                <VehicleFormField name="vehicle_type" label="Araç türü" value={formData.vehicle_type} onChange={handleFormFieldChange} />
                <VehicleFormField name="transport_type" label="Taşıt türü" value={formData.transport_type} onChange={handleFormFieldChange} />
                <VehicleFormField name="current_km" label="Mevcut KM" value={formData.current_km} onChange={handleFormFieldChange} />
              </FormSection>
              )}

              {activeFormTab === 'general' && (
              <FormSection title="Teknik Özellikler" description="Motor, şanzıman ve model detayları">
                <VehicleFormField name="package_name" label="Paket" value={formData.package_name} onChange={handleFormFieldChange} />
                <VehicleFormField name="transmission" label="Vites" value={formData.transmission} onChange={handleFormFieldChange} />
                <VehicleFormField name="fuel_type" label="Yakıt türü" value={formData.fuel_type} onChange={handleFormFieldChange} />
                <VehicleFormField name="model_year" label="Model yılı" value={formData.model_year} onChange={handleFormFieldChange} />
              </FormSection>
              )}

              {activeFormTab === 'license' && (
              <FormSection title="Ruhsat & Tescil" description="Resmi kayıt ve şasi bilgileri">
                <VehicleFormField name="license_number" label="Ruhsat no" value={formData.license_number} onChange={handleFormFieldChange} />
                <VehicleFormField name="registration_date" label="Tescil tarihi" value={formData.registration_date} onChange={handleFormFieldChange} />
                <VehicleFormField name="engine_number" label="Motor no" value={formData.engine_number} onChange={handleFormFieldChange} />
                <VehicleFormField name="chassis_number" label="Şasi no" value={formData.chassis_number} onChange={handleFormFieldChange} />
              </FormSection>
              )}

              {activeFormTab === 'insurance' && (
              <FormSection title="Sigorta & Muayene" description="Poliçe bitişleri ve muayene tarihleri (muayene modülünden senkron)">
                <VehicleFormField name="traffic_policy_date" label="Trafik poliçesi bitiş" value={formData.traffic_policy_date} onChange={handleFormFieldChange} />
                <VehicleFormField name="insurance_policy_date" label="Kasko poliçesi bitiş" value={formData.insurance_policy_date} onChange={handleFormFieldChange} />
                <VehicleFormField name="first_inspection_date" label="1. TÜVTÜRK tarihi" value={formData.first_inspection_date} onChange={handleFormFieldChange} />
                <VehicleFormField name="exhaust_inspection_date" label="Egzoz tarihi" value={formData.exhaust_inspection_date} onChange={handleFormFieldChange} />
              </FormSection>
              )}

              {activeFormTab === 'custody' && (
              <FormSection title="Araç Zimmet" description="Zimmetli kişi, departman ve demirbaş formu">
                <VehicleFormField name="assigned_person" label="Araç kime zimmetli?" value={formData.assigned_person} onChange={handleFormFieldChange} />
                <VehicleFormField name="custody_date" label="Zimmet tarihi" value={formData.custody_date} onChange={handleFormFieldChange} />
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-gray-600 dark:text-gray-300">Tesis (otel)</Label>
                  <Input
                    readOnly
                    disabled
                    className={`${fieldInputClass} cursor-not-allowed bg-gray-50 dark:bg-gray-900/50`}
                    value={selectedHotelName || 'Önce tesis seçin'}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-gray-600 dark:text-gray-300">Departman</Label>
                  <select
                    className={fieldSelectClass}
                    value={formData.custody_department}
                    onChange={(e) => setFormData((prev) => ({ ...prev, custody_department: e.target.value }))}
                    disabled={!formData.hotel_id}
                  >
                    <option value="">{formData.hotel_id ? 'Departman seçin' : 'Önce tesis seçin'}</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>{dept.department_name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5 sm:col-span-2 xl:col-span-3">
                  <Label className="text-xs font-medium text-gray-600 dark:text-gray-300">Demirbaş zimmet formu</Label>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => custodyFormInputRef.current?.click()}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') custodyFormInputRef.current?.click(); }}
                    className="flex min-h-[88px] cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/80 px-4 py-4 transition hover:border-blue-400 hover:bg-blue-50/30 dark:border-gray-600 dark:bg-gray-900/30 dark:hover:border-blue-500/50 dark:hover:bg-blue-950/20"
                  >
                    <DocumentArrowUpIcon className="h-6 w-6 text-gray-400" />
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                      {custodyFormName || 'PDF, Word veya görsel yüklemek için tıklayın'}
                    </span>
                    {editingRow?.custody_form_url ? (
                      <a
                        href={editingRow.custody_form_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs text-blue-600 hover:underline dark:text-blue-400"
                      >
                        Mevcut formu görüntüle
                      </a>
                    ) : null}
                  </div>
                  <input
                    ref={custodyFormInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    className="sr-only"
                    onChange={handleCustodyFormChange}
                  />
                </div>
              </FormSection>
              )}
            </div>

            <div className="flex shrink-0 items-center justify-end gap-3 border-t border-gray-100 bg-gray-50/80 px-5 py-4 dark:border-gray-700 dark:bg-gray-900/40">
              <Button variant="outline" type="button" onClick={closeModal}>
                İptal
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={saving}>
                {saving ? 'Kaydediliyor...' : 'Kaydet'}
              </Button>
            </div>
          </form>
        </div>
      </ResponsiveModal>
    </VehiclePageShell>
  );
};

export default InternalAuditVehiclesPage;
